import React from 'react';
import { Task } from '@/app/task-bank/public-actions';
import { cn } from '@/lib/utils';
import Image from 'next/image';

interface TaskCardRendererProps {
  task: Task;
  taskType: 'task1' | 'task2';
  selectedTaskId?: string | null;
  onSelectTask: (task: Task) => void;
}

export function TaskCardRenderer({
  task,
  taskType,
  selectedTaskId,
  onSelectTask,
}: TaskCardRendererProps) {
  const handleTaskSelect = () => {
    onSelectTask(task);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleTaskSelect();
    }
  };

  const baseCardClasses = cn(
    'border rounded-lg p-4 cursor-pointer transition-colors',
    'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    selectedTaskId === task.id && 'border-primary bg-primary/5'
  );

  if (taskType === 'task1') {
    return (
      <div
        key={task.id}
        className={cn(baseCardClasses, 'flex flex-col space-y-2')}
        onClick={handleTaskSelect}
        role='button'
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {task.imageUrl && (
          <div className='relative h-48 w-full overflow-hidden rounded-md border'>
            <Image
              src={task.imageUrl}
              alt={task.prompt || 'Task 1 visual'}
              className='object-contain w-full h-full'
              width={400}
              height={192}
            />
          </div>
        )}
        <div className='flex space-x-2'>
          {task.task1Type && (
            <span className='text-xs bg-primary/10 text-primary px-2 py-1 rounded-full'>
              {task.task1Type}
            </span>
          )}
          {task.visualType && (
            <span className='text-xs bg-secondary/10 text-secondary px-2 py-1 rounded-full'>
              {task.visualType}
            </span>
          )}
        </div>
        <p className='text-sm font-medium line-clamp-3'>{task.prompt}</p>
      </div>
    );
  }

  return (
    <div
      key={task.id}
      className={baseCardClasses}
      onClick={handleTaskSelect}
      role='button'
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <p className='text-sm font-medium'>{task.prompt}</p>
      <div className='flex flex-wrap gap-1 mt-2'>
        {task.questionType && (
          <span className='text-xs bg-primary/10 text-primary px-2 py-1 rounded-full'>
            {task.questionType.replace(/_/g, ' ')}
          </span>
        )}
      </div>
    </div>
  );
}
